import { useMemo, useState } from 'react';

import { Search } from 'lucide-react';

import { PromptSection } from '@/components/prompt-library/prompt-section';
import { usePageBar } from '@/components/layout/page-bar';
import { Input } from '@/components/ui/input';
import { useConnectors } from '@/hooks/useConnectors';
import { useResponsive } from '@/hooks/useResponsive';
import { useScrollRestoration } from '@/hooks/useScrollRestoration';
import { cn } from '@/lib/utils';
import {
  CATEGORIES,
  getAllPrompts,
  getAvailableConnectorIds,
  isPromptAvailable,
  type Prompt,
} from '@/lib/prompt-lib-utils';

export default function PromptLibraryPage() {
  const { isMobile } = useResponsive();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');

  // Remember the list scroll position so returning from a prompt detail
  // (browser-back or breadcrumb) lands the user where they were, not the top.
  useScrollRestoration();

  const allPrompts = useMemo(() => getAllPrompts(), []);

  // Live connector availability — prompts tagged with a `connector` only show when that
  // connector is currently `available`. Hidden ones reappear automatically when it ships.
  const { connectors } = useConnectors();
  const availableConnectors = useMemo(
    () => getAvailableConnectorIds(connectors),
    [connectors]
  );

  // Filter prompts based on connector availability, search and category
  const filteredPrompts = useMemo(() => {
    let prompts = allPrompts;

    // Hide prompts whose connector isn't currently available (untagged prompts always show)
    prompts = prompts.filter((p) => isPromptAvailable(p, availableConnectors));

    // Filter by search query
    if (searchQuery.trim()) {
      const lowerQuery = searchQuery.toLowerCase();
      prompts = prompts.filter(
        (p) =>
          p.title.toLowerCase().includes(lowerQuery) ||
          p.description.toLowerCase().includes(lowerQuery)
      );
    }

    // Filter by category
    if (selectedCategory !== 'all') {
      prompts = prompts.filter((p) => p.category === selectedCategory);
    }

    // Group by category
    const grouped = new Map<string, Prompt[]>();
    for (const cat of CATEGORIES) {
      grouped.set(cat.id, []);
    }
    for (const prompt of prompts) {
      const existing = grouped.get(prompt.category) || [];
      existing.push(prompt);
      grouped.set(prompt.category, existing);
    }

    return grouped;
  }, [allPrompts, availableConnectors, searchQuery, selectedCategory]);

  // Shell page bar: title + page-level search. Category pills/dropdown and the
  // description stay in content.
  usePageBar({
    title: 'Prompt Library',
    description: 'Pick an idea, tweak it in a sentence, and start building.',
    actions: (
      <div className="relative w-full min-w-[6rem] flex-1 sm:w-56 sm:flex-none">
        <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          type="text"
          placeholder="Search prompts..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="h-9 pl-8 text-sm"
        />
      </div>
    ),
  });

  return (
    <div className="min-h-full">
      <div className={cn(
        "w-full",
        isMobile ? "px-4 py-6" : "px-6 sm:px-8 lg:px-10 pt-6 pb-10"
      )}>
        {/* Header (title + search live in the shell PageBar) */}
        <div className={cn(isMobile ? "mb-5" : "mb-10")}>
          {/* Category pills — wrap on desktop, horizontal scroll on mobile
              (matches App Library / Skills). No filter dropdown. */}
          <div
            className={cn(
              'flex items-center gap-2',
              isMobile
                ? '-mx-4 overflow-x-auto px-4 pb-1'
                : 'flex-wrap'
            )}
          >
            <button
              onClick={() => setSelectedCategory('all')}
              className={cn(
                'px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors duration-150 whitespace-nowrap flex-none',
                selectedCategory === 'all'
                  ? 'bg-foreground text-background border-foreground'
                  : 'bg-card text-muted-foreground border-border hover:text-foreground hover:bg-muted'
              )}
            >
              All
            </button>
            {CATEGORIES.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setSelectedCategory(cat.id)}
                className={cn(
                  'px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors duration-150 whitespace-nowrap flex-none',
                  selectedCategory === cat.id
                    ? 'bg-foreground text-background border-foreground'
                    : 'bg-card text-muted-foreground border-border hover:text-foreground hover:bg-muted'
                )}
              >
                {cat.label}
              </button>
            ))}
          </div>
        </div>

        {/* Prompt Sections */}
        {CATEGORIES.map((category) => {
          const prompts = filteredPrompts.get(category.id) || [];
          if (selectedCategory !== 'all' && selectedCategory !== category.id) {
            return null;
          }
          return (
            <PromptSection
              key={category.id}
              title={category.label}
              prompts={prompts}
              isMobile={isMobile}
            />
          );
        })}

        {/* Empty state */}
        {allPrompts.length === 0 && (
          <div className={cn("text-center", isMobile ? "py-12" : "py-20")}>
            <p className={cn("text-muted-foreground", isMobile && "text-sm")}>
              No prompts available yet.
            </p>
          </div>
        )}

        {/* No results */}
        {allPrompts.length > 0 &&
          Array.from(filteredPrompts.values()).every(
            (arr) => arr.length === 0
          ) && (
            <div className={cn("text-center", isMobile ? "py-12" : "py-20")}>
              <p className={cn("text-muted-foreground", isMobile && "text-sm")}>
                No prompts found matching your search.
              </p>
            </div>
          )}
      </div>
    </div>
  );
}
